/**
 * v1.14.63.1 — D&O Invites Panel (admin)
 * Lista inviti + dettaglio + compile + transmit (broker-gated)
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Link as LinkIcon, Copy, Send, X, RefreshCw,
          FileText, Download, ArrowLeft, Sparkles, Loader2 } from "lucide-react";
import DOQuestionnaireFormPage from "./DOQuestionnaireFormPage";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/admin/do`;

const STATUS_LABEL = {
  draft: { label: "Bozza (in attesa cliente)", color: "bg-slate-100 text-slate-700" },
  compilato: { label: "Compilato dal cliente", color: "bg-amber-100 text-amber-800" },
  pending_broker_review: { label: "🌐 Richiesta spontanea web", color: "bg-violet-100 text-violet-800" },
  transmitted: { label: "Trasmesso al cliente", color: "bg-emerald-100 text-emerald-800" },
  revoked: { label: "Revocato", color: "bg-red-100 text-red-700" },
  expired: { label: "Scaduto", color: "bg-slate-100 text-slate-500" },
};

export default function DOInvitesPanel() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | detail | new_invite | manual
  const [selected, setSelected] = useState(null);

  const loadList = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE}/invites`, { withCredentials: true });
      setInvites(data.items || []);
    } catch (e) {
      toast.error("Impossibile caricare gli inviti");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadList(); }, []);

  if (view === "new_invite") {
    return <NewInviteForm onCreated={(inv) => {
      loadList();
      setView("list");
      toast.success("Invito creato. Link copiabile nella lista.");
    }} onBack={() => setView("list")} />;
  }

  if (view === "manual") {
    return (
      <div className="space-y-3">
        <Button variant="outline" size="sm" onClick={() => setView("list")} data-testid="do-back-to-invites">
          <ArrowLeft className="w-4 h-4 mr-1" /> Torna alla lista inviti
        </Button>
        <DOQuestionnaireFormPage />
      </div>
    );
  }

  if (view === "detail" && selected) {
    return <DOInviteDetail
      qid={selected.id}
      onBack={() => { setView("list"); loadList(); }}
    />;
  }

  return (
    <div className="space-y-4 max-w-6xl mx-auto p-4" data-testid="do-invites-panel">
      {/* v1.15.0.1 — Banner disambiguazione: le pratiche web pubblico
          (source=public_open_v3) NON compaiono qui perché l'endpoint
          /api/admin/do/invites filtra solo inviti broker-gated + public_open
          legacy. Le 270+ pratiche complete sono su /admin/pratiche-do. */}
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2"
            data-testid="do-invites-info-banner">
        <span className="mt-0.5">ℹ️</span>
        <div className="flex-1">
          <strong>Questo pannello mostra solo inviti agente-gated e vecchie
          submission pubbliche legacy.</strong> Le pratiche D&amp;O compilate dai
          clienti tramite il sito pubblico <code>/do</code> sono elencate su{" "}
          <a href="/admin/pratiche-do"
              data-testid="do-invites-link-pratiche"
              className="underline font-semibold text-[#163321] hover:text-[#0F1F14]">
            📄 Pratiche D&amp;O compilate
          </a>
          {" "}(menu CRM · Richieste &amp; Operatività).
        </div>
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-[#163321]">Inviti D&amp;O Multi-Compagnia</CardTitle>
            <p className="text-sm text-slate-500">
              Crea un link da inviare al cliente. Compila lui, tu generi i PDF e li trasmetti.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setView("manual")}
                     data-testid="do-btn-manual-mode">
              <FileText className="w-4 h-4 mr-1" /> Compila manualmente
            </Button>
            <Button onClick={() => setView("new_invite")}
                     data-testid="do-btn-new-invite"
                     className="bg-[#163321] hover:bg-[#1d4a2e] text-white">
              <Plus className="w-4 h-4 mr-1" /> Nuovo invito cliente
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading
            ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#163321]" /></div>
            : invites.length === 0
              ? <p className="text-sm text-slate-500 text-center py-8">Nessun invito ancora. Clicca "Nuovo invito cliente" per cominciare.</p>
              : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-slate-500 uppercase border-b">
                        <th className="p-2">Cliente</th>
                        <th className="p-2">Status</th>
                        <th className="p-2">Creato</th>
                        <th className="p-2">Scadenza</th>
                        <th className="p-2">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invites.map((inv) => (
                        <tr key={inv.id} className="border-b hover:bg-slate-50"
                             data-testid={`do-invite-row-${inv.id}`}>
                          <td className="p-2">
                            <p className="font-semibold text-[#163321]">{inv.client_name || "(senza nome)"}</p>
                            <p className="text-xs text-slate-500">{inv.client_email || "—"}</p>
                          </td>
                          <td className="p-2">
                            <span className={`text-xs px-2 py-1 rounded-full ${STATUS_LABEL[inv.status]?.color}`}>
                              {STATUS_LABEL[inv.status]?.label || inv.status}
                            </span>
                          </td>
                          <td className="p-2 text-xs text-slate-500">
                            {new Date(inv.created_at).toLocaleDateString("it-IT")}
                          </td>
                          <td className="p-2 text-xs text-slate-500">
                            {inv.expires_at ? new Date(inv.expires_at).toLocaleDateString("it-IT") : "—"}
                          </td>
                          <td className="p-2 flex gap-1 flex-wrap">
                            <Button variant="outline" size="sm"
                                     data-testid={`do-copy-link-${inv.id}`}
                                     onClick={() => {
                                       navigator.clipboard.writeText(inv.public_url || "");
                                       toast.success("Link copiato");
                                     }}>
                              <Copy className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm"
                                     data-testid={`do-open-detail-${inv.id}`}
                                     onClick={() => { setSelected(inv); setView("detail"); }}
                                     className="bg-[#163321] hover:bg-[#1d4a2e] text-white">
                              Apri
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
        </CardContent>
      </Card>
    </div>
  );
}

function NewInviteForm({ onCreated, onBack }) {
  const [form, setForm] = useState({ client_name: "", client_email: "", note: "", ttl_days: 30 });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${BASE}/invites`, form, { withCredentials: true });
      onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione invito");
    } finally { setSaving(false); }
  };
  return (
    <Card className="max-w-xl mx-auto" data-testid="do-new-invite-form">
      <CardHeader>
        <Button variant="outline" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Annulla
        </Button>
        <CardTitle className="text-[#163321] mt-3">Nuovo invito D&amp;O</CardTitle>
        <p className="text-sm text-slate-500">
          Genera un link `/q/do/{`{token}`}` da inviare al cliente via email/WhatsApp.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Nome cliente</Label>
          <Input data-testid="do-invite-client-name"
                  value={form.client_name}
                  onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                  placeholder="ACME SPA o Mario Rossi" />
        </div>
        <div>
          <Label>Email cliente</Label>
          <Input type="email"
                  data-testid="do-invite-client-email"
                  value={form.client_email}
                  onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                  placeholder="mario@acme.it" />
        </div>
        <div>
          <Label>Validità link (giorni)</Label>
          <Input type="number" min="1" max="365"
                  data-testid="do-invite-ttl"
                  value={form.ttl_days}
                  onChange={(e) => setForm({ ...form, ttl_days: parseInt(e.target.value) || 30 })} />
        </div>
        <div>
          <Label>Note interne</Label>
          <Input data-testid="do-invite-note"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Es. RAL 2M, scadenza polizza Generali..." />
        </div>
        <Button onClick={submit} disabled={saving || !form.client_name}
                 data-testid="do-invite-create-btn"
                 className="w-full bg-[#163321] hover:bg-[#1d4a2e] text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LinkIcon className="w-4 h-4 mr-2" /> Genera link cliente</>}
        </Button>
      </CardContent>
    </Card>
  );
}

function DOInviteDetail({ qid, onBack }) {
  const [invite, setInvite] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [selectedCompagnie, setSelectedCompagnie] = useState([]);
  const [compiling, setCompiling] = useState(false);
  const [transmitting, setTransmitting] = useState(false);

  const reload = async () => {
    const { data } = await axios.get(`${BASE}/questionnaires/${qid}`, { withCredentials: true });
    setInvite(data);
    if (data.compagnie_target && data.compagnie_target.length > 0) {
      setSelectedCompagnie(data.compagnie_target);
    } else if (templates) {
      setSelectedCompagnie(Object.keys(templates.compagnie));
    }
  };

  useEffect(() => {
    axios.get(`${BASE}/templates`, { withCredentials: true })
      .then((r) => {
        setTemplates(r.data);
        setSelectedCompagnie(Object.keys(r.data.compagnie));
      });
    reload();
  }, [qid]);

  const compile = async () => {
    if (selectedCompagnie.length === 0) { toast.error("Seleziona almeno una compagnia"); return; }
    setCompiling(true);
    try {
      const { data } = await axios.post(
        `${BASE}/questionnaires/${qid}/compile`,
        { compagnie: selectedCompagnie },
        { withCredentials: true });
      const ok = data.compiled.filter((p) => !p.error).length;
      toast.success(`Generati ${ok}/${data.count} PDF`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Errore");
    } finally { setCompiling(false); }
  };

  const transmit = async () => {
    setTransmitting(true);
    try {
      await axios.post(`${BASE}/invites/${qid}/transmit`, {}, { withCredentials: true });
      toast.success("Trasmesso al cliente! Ora vede i PDF dal suo link.");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Errore");
    } finally { setTransmitting(false); }
  };

  if (!invite || !templates) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-4 max-w-5xl mx-auto p-4" data-testid="do-invite-detail">
      <Button variant="outline" size="sm" onClick={onBack}>
        <ArrowLeft className="w-4 h-4 mr-1" /> Torna alla lista
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-[#163321]">
            {invite.client_name || "(cliente senza nome)"}
            <span className={`ml-3 text-xs px-2 py-1 rounded-full ${STATUS_LABEL[invite.status]?.color}`}>
              {STATUS_LABEL[invite.status]?.label}
            </span>
          </CardTitle>
          <p className="text-sm text-slate-500">{invite.client_email || "—"}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Form data preview */}
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-xs font-bold text-slate-600 uppercase mb-2">Dati compilati dal cliente</p>
            {invite.form_data && Object.keys(invite.form_data).length > 0
              ? <pre className="text-xs overflow-auto max-h-64" data-testid="do-form-data-preview">{JSON.stringify(invite.form_data, null, 2)}</pre>
              : <p className="text-sm text-slate-400 italic">Il cliente non ha ancora compilato.</p>}
          </div>

          {/* Compagnie selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Object.entries(templates.compagnie).map(([slug, c]) => (
              <label key={slug} className="flex items-start gap-2 p-3 border rounded-lg cursor-pointer hover:bg-slate-50"
                      data-testid={`do-detail-comp-${slug}`}>
                <Checkbox checked={selectedCompagnie.includes(slug)}
                           onCheckedChange={() =>
                             setSelectedCompagnie((prev) =>
                               prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug])
                           } />
                <div>
                  <p className="text-sm font-bold text-[#163321]">{c.name}</p>
                  <p className="text-xs text-slate-500">{c.campi_supportati.length} campi</p>
                </div>
              </label>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-2 flex-wrap pt-3 border-t">
            <Button onClick={compile} disabled={compiling || selectedCompagnie.length === 0}
                     data-testid="do-detail-compile-btn"
                     className="bg-amber-500 hover:bg-amber-600 text-white">
              {compiling ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" /> Genera PDF</>}
            </Button>
            <Button onClick={transmit} disabled={transmitting || !invite.compiled_pdfs?.length || invite.status === "transmitted"}
                     data-testid="do-detail-transmit-btn"
                     className="bg-[#163321] hover:bg-[#1d4a2e] text-white">
              {transmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Trasmetti al cliente</>}
            </Button>
          </div>

          {/* PDF download links */}
          {invite.compiled_pdfs && invite.compiled_pdfs.length > 0 && (
            <div className="pt-3 border-t">
              <p className="text-xs font-bold text-slate-600 uppercase mb-2">PDF generati</p>
              <div className="space-y-1">
                {invite.compiled_pdfs.map((p) => (
                  <a key={p.company_slug}
                      href={`${process.env.REACT_APP_BACKEND_URL}${p.download_url}`}
                      target="_blank" rel="noreferrer"
                      data-testid={`do-detail-dl-${p.company_slug}`}
                      className="flex items-center gap-2 p-2 rounded bg-amber-50 hover:bg-amber-100 text-sm text-[#163321]">
                    <Download className="w-4 h-4" />
                    <span className="flex-1">{p.company_name || p.company_slug}</span>
                    {p.size_bytes && <span className="text-xs text-slate-500">{Math.round(p.size_bytes / 1024)}KB</span>}
                  </a>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
